import React from 'react';

const TimezoneTest = () => {
  // Test data from the backend (the appointments causing the issue)
  const testAppointments = [
    {
      id: '1',
      customer_name: 'Matt Don',
      pet_name: 'Molly',
      appointment_time: '2025-09-20T18:30', // Stored in UTC, should display as 2:30 PM Eastern
      description: 'Test appointment 1'
    },
    {
      id: '2', 
      customer_name: 'Sam Manny',
      pet_name: 'Kitty',
      appointment_time: '2025-09-20T21:30', // Stored in UTC, should display as 5:30 PM Eastern
      description: 'Test appointment 2'
    }
  ];

  const formatTimeWithTimezone = (dateString) => {
    if (!dateString) return 'Time not available';
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return 'Invalid time';
      }
      
      // Convert from UTC to Eastern Time (business timezone)
      const easternTime = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
      
      return easternTime;
      
    } catch (error) {
      console.error('Error formatting appointment time:', dateString, error);
      return 'Time error';
    }
  };

  const formatTimeWithoutTimezone = (dateString) => {
    if (!dateString) return 'Time not available';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Time error';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Timezone Conversion Test</h1>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Appointment Time Conversion Test</h2>
          
          <div className="space-y-6">
            {testAppointments.map((appointment) => (
              <div key={appointment.id} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">
                  {appointment.customer_name} - {appointment.pet_name}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Raw Time (UTC):</span>
                    <p className="text-gray-600">{appointment.appointment_time}</p>
                  </div>
                  
                  <div>
                    <span className="font-medium text-red-700">OLD (Without Timezone):</span>
                    <p className="text-red-600 font-semibold">
                      {formatTimeWithoutTimezone(appointment.appointment_time)}
                    </p>
                    <p className="text-xs text-red-500">❌ Shows wrong time</p>
                  </div>
                  
                  <div>
                    <span className="font-medium text-green-700">NEW (With Timezone):</span>
                    <p className="text-green-600 font-semibold">
                      {formatTimeWithTimezone(appointment.appointment_time)}
                    </p>
                    <p className="text-xs text-green-500">✅ Shows correct time</p>
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Expected Result:</strong> {appointment.customer_name === 'Matt Don' ? '2:30 PM' : '5:30 PM'} Eastern Time
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-medium text-yellow-800 mb-2">Timezone Fix Explanation:</h3>
            <p className="text-sm text-yellow-700">
              The appointments were stored in UTC time but displayed without timezone conversion. 
              The new implementation uses <code>timeZone: 'America/New_York'</code> to properly 
              convert UTC times to Eastern Time for display, fixing the 4-hour offset issue.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimezoneTest;